import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Flex,
  Loader,
  Text,
  TextField,
  IconButton,
} from '@vibe/core';
import { Add, CloseSmall, Search } from '@vibe/icons';
import {
  getPermissionRules,
  createPermissionRule,
  createPermissionRulesBulk,
  deletePermissionRule,
  type PermissionRule,
  type PermissionType,
  type TargetType,
} from '../../api/permission.api';
import { type MondayUser } from '../../api/monday.api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Constants ──────────────────────────────────────────────────────────────────

const SEARCH_MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const USERS_BATCH_SIZE = 1000;
const MAX_DROPDOWN_RESULTS = 20;
const TABLE_PAGE_SIZE = 10;

const USERS_PAGE_QUERY = `
  query GetUsers($limit: Int!, $page: Int!) {
    users(kind: non_guests, limit: $limit, page: $page) {
      id
      name
      email
      photo_thumb_small
      title
      teams {
        id
        name
      }
    }
  }
`;

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  permission: PermissionType;
  /** Label for the grantee column, e.g. "Approver(s)" or "Viewer(s)" */
  granteeLabel: string;
}

interface TeamOption {
  id: string;
  name: string;
}

interface ResolvedUserRow {
  mondayUserId: string;
  name: string;
  email: string;
  grantees: Array<{
    granteeType: string;
    granteeId: string;
    source: 'direct' | 'team' | 'all';
    ruleId: string;
  }>;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Fetch a batch of users from monday.com and filter client-side.
 * monday.com doesn't support server-side text search for users.
 */
async function searchMondayUsers(query: string): Promise<MondayUser[]> {
  const allUsers: MondayUser[] = [];
  const matched: MondayUser[] = [];
  const lowerQ = query.toLowerCase();
  let page = 1;

  // Fetch up to 2 pages (2000 users max) and filter client-side
  while (page <= 2) {
    const response = await monday.api(USERS_PAGE_QUERY, {
      variables: { limit: USERS_BATCH_SIZE, page },
    });

    const data = response.data as {
      users?: Array<{
        id: number | string;
        name: string;
        email: string;
        photo_thumb_small: string | null;
        title: string | null;
        teams?: Array<{ id: number | string; name: string }>;
      }>;
    };

    const users = data.users ?? [];
    if (users.length === 0) break;

    for (const u of users) {
      const user: MondayUser = {
        id: Number(u.id),
        name: u.name,
        email: u.email,
        photo_thumb_small: u.photo_thumb_small ?? null,
        title: u.title ?? null,
        teams: (u.teams ?? []).map((t) => ({ id: Number(t.id), name: t.name })),
      };
      allUsers.push(user);

      if (
        u.name.toLowerCase().includes(lowerQ) ||
        u.email.toLowerCase().includes(lowerQ)
      ) {
        matched.push(user);
      }
      if (matched.length >= MAX_DROPDOWN_RESULTS) break;
    }

    if (matched.length >= MAX_DROPDOWN_RESULTS || users.length < USERS_BATCH_SIZE) break;
    page++;
  }

  return matched;
}

/**
 * Fetch a specific set of users by their IDs.
 * Used to resolve names for users referenced in rules.
 */
async function fetchUsersByIds(userIds: string[]): Promise<MondayUser[]> {
  if (userIds.length === 0) return [];

  // Fetch users in batches and filter to only those we need
  const needed = new Set(userIds);
  const found: MondayUser[] = [];
  let page = 1;

  while (found.length < needed.size && page <= 5) {
    const response = await monday.api(USERS_PAGE_QUERY, {
      variables: { limit: USERS_BATCH_SIZE, page },
    });

    const data = response.data as {
      users?: Array<{
        id: number | string;
        name: string;
        email: string;
        photo_thumb_small: string | null;
        title: string | null;
        teams?: Array<{ id: number | string; name: string }>;
      }>;
    };

    const users = data.users ?? [];
    if (users.length === 0) break;

    for (const u of users) {
      if (needed.has(String(u.id))) {
        found.push({
          id: Number(u.id),
          name: u.name,
          email: u.email,
          photo_thumb_small: u.photo_thumb_small ?? null,
          title: u.title ?? null,
          teams: (u.teams ?? []).map((t) => ({ id: Number(t.id), name: t.name })),
        });
      }
    }

    if (users.length < USERS_BATCH_SIZE) break;
    page++;
  }

  return found;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const PermissionRulesTab: React.FC<Props> = ({ permission, granteeLabel }) => {
  // ─── State ──────────────────────────────────────────────────────────────────
  const [rules, setRules] = useState<PermissionRule[]>([]);
  const [userCache, setUserCache] = useState<Map<string, MondayUser>>(new Map());
  const [teamCache, setTeamCache] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add rule form state. Grantee is always a user — team grantees are no
  // longer offered for new rules (see `granteeTypeOptions` below).
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTargetType, setNewTargetType] = useState<TargetType>('all');
  const [newTargetId, setNewTargetId] = useState<string | null>(null);
  const [newGranteeId, setNewGranteeId] = useState<string | null>(null);
  const [isAddingSaving, setIsAddingSaving] = useState(false);

  // Per-user assignment form state (multi-select)
  const [showAssignForm, setShowAssignForm] = useState(false);
  type DropdownOptionObj = { value: string; label: string };
  const [assignTargets, setAssignTargets] = useState<DropdownOptionObj[]>([]);
  const [assignGrantees, setAssignGrantees] = useState<DropdownOptionObj[]>([]);
  const [isAssignSaving, setIsAssignSaving] = useState(false);

  // User search state (for dropdowns)
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<MondayUser[]>([]);
  const [isUserSearching, setIsUserSearching] = useState(false);
  const debouncedUserSearch = useDebouncedValue(userSearchQuery, DEBOUNCE_MS);

  // Grantee search state
  const [granteeSearchQuery, setGranteeSearchQuery] = useState('');
  const [granteeSearchResults, setGranteeSearchResults] = useState<MondayUser[]>([]);
  const [isGranteeSearching, setIsGranteeSearching] = useState(false);
  const debouncedGranteeSearch = useDebouncedValue(granteeSearchQuery, DEBOUNCE_MS);

  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  // Teams state (loaded once for team dropdowns)
  const [allTeams, setAllTeams] = useState<TeamOption[]>([]);

  // Table search & pagination state
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [tablePage, setTablePage] = useState(0);

  // ─── Load data ──────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const existingRules = await getPermissionRules(permission);
      setRules(existingRules);

      // Collect all user IDs referenced in rules to resolve names
      const referencedUserIds = new Set<string>();
      for (const r of existingRules) {
        if (r.targetType === 'user' && r.targetId) referencedUserIds.add(r.targetId);
        if (r.granteeType === 'user') referencedUserIds.add(r.granteeId);
      }

      // Fetch referenced users to populate name cache
      if (referencedUserIds.size > 0) {
        const users = await fetchUsersByIds(Array.from(referencedUserIds));
        const cache = new Map<string, MondayUser>();
        const teams = new Map<string, string>();
        for (const u of users) {
          cache.set(String(u.id), u);
          for (const t of u.teams) {
            teams.set(String(t.id), t.name);
          }
        }
        setUserCache(cache);
        setTeamCache(teams);
      }

      // Also load first batch of users to extract teams for team dropdown
      const firstBatch = await monday.api(USERS_PAGE_QUERY, {
        variables: { limit: USERS_BATCH_SIZE, page: 1 },
      });
      const firstData = firstBatch.data as {
        users?: Array<{
          id: number | string;
          teams?: Array<{ id: number | string; name: string }>;
        }>;
      };
      const teamsMap = new Map<string, string>();
      for (const u of firstData.users ?? []) {
        for (const t of u.teams ?? []) {
          teamsMap.set(String(t.id), t.name);
        }
      }
      setAllTeams(
        Array.from(teamsMap.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      // Merge into teamCache
      setTeamCache((prev) => {
        const next = new Map(prev);
        for (const [id, name] of teamsMap) next.set(id, name);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [permission]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── User search effects ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!debouncedUserSearch || debouncedUserSearch.length < SEARCH_MIN_CHARS) {
      setUserSearchResults([]);
      return;
    }
    let cancelled = false;
    setIsUserSearching(true);
    searchMondayUsers(debouncedUserSearch)
      .then((results) => {
        if (!cancelled) setUserSearchResults(results);
      })
      .catch(() => { /* silently fail */ })
      .finally(() => {
        if (!cancelled) setIsUserSearching(false);
      });
    return () => { cancelled = true; };
  }, [debouncedUserSearch]);

  useEffect(() => {
    if (!debouncedGranteeSearch || debouncedGranteeSearch.length < SEARCH_MIN_CHARS) {
      setGranteeSearchResults([]);
      return;
    }
    let cancelled = false;
    setIsGranteeSearching(true);
    searchMondayUsers(debouncedGranteeSearch)
      .then((results) => {
        if (!cancelled) setGranteeSearchResults(results);
      })
      .catch(() => { /* silently fail */ })
      .finally(() => {
        if (!cancelled) setIsGranteeSearching(false);
      });
    return () => { cancelled = true; };
  }, [debouncedGranteeSearch]);

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const getUserName = useCallback((userId: string): string => {
    const user = userCache.get(userId);
    return user ? `${user.name}` : `User ${userId}`;
  }, [userCache]);

  const getTeamName = useCallback((teamId: string): string => {
    return teamCache.get(teamId) ?? `Team ${teamId}`;
  }, [teamCache]);

  const formatTarget = useCallback((rule: PermissionRule): string => {
    if (rule.targetType === 'all') return 'All users';
    if (rule.targetType === 'team') return `Team: ${getTeamName(rule.targetId ?? '')}`;
    return getUserName(rule.targetId ?? '');
  }, [getUserName, getTeamName]);

  const formatGrantee = useCallback((rule: PermissionRule): string => {
    if (rule.granteeType === 'team') return `Team: ${getTeamName(rule.granteeId)}`;
    return getUserName(rule.granteeId);
  }, [getUserName, getTeamName]);

  const formatGranteeName = useCallback((granteeType: string, granteeId: string): string => {
    if (granteeType === 'team') return `Team: ${getTeamName(granteeId)}`;
    return getUserName(granteeId);
  }, [getUserName, getTeamName]);

  // Add user to cache when selected from search
  const cacheUser = useCallback((user: MondayUser) => {
    setUserCache((prev) => {
      const next = new Map(prev);
      next.set(String(user.id), user);
      return next;
    });
  }, []);

  // ─── Dropdown options ─────────────────────────────────────────────────────────
  const targetTypeOptions = [
    { value: 'all', label: 'All users' },
    { value: 'team', label: 'Team' },
  ];

  const teamOptions = useMemo(
    () => allTeams.map((t) => ({ value: t.id, label: t.name })),
    [allTeams],
  );

  // Team grantees are intentionally not offered anymore — only individual
  // users can be granted permissions. Existing team-grantee rules still
  // display and function (see formatGranteeName / resolvedRules below), but
  // new rules must target a specific user. The Add Rule form only shows a
  // user picker; the previous "Grantee type" dropdown was removed.

  const userSearchOptions = useMemo(() => {
    const fromSearch = userSearchResults.map((u) => ({
      value: String(u.id),
      label: `${u.name} (${u.email})`,
    }));
    // Keep currently selected value in list so it doesn't vanish
    if (newTargetId) {
      const exists = fromSearch.some((o) => o.value === newTargetId);
      if (!exists) {
        const cached = userCache.get(newTargetId);
        if (cached) {
          fromSearch.unshift({ value: String(cached.id), label: `${cached.name} (${cached.email})` });
        }
      }
    }
    return fromSearch;
  }, [userSearchResults, newTargetId, userCache]);

  const granteeSearchOptions = useMemo(() => {
    const fromSearch = granteeSearchResults.map((u) => ({
      value: String(u.id),
      label: `${u.name} (${u.email})`,
    }));
    if (newGranteeId) {
      const exists = fromSearch.some((o) => o.value === newGranteeId);
      if (!exists) {
        const cached = userCache.get(newGranteeId);
        if (cached) {
          fromSearch.unshift({ value: String(cached.id), label: `${cached.name} (${cached.email})` });
        }
      }
    }
    return fromSearch;
  }, [granteeSearchResults, newGranteeId, userCache]);

  // Merge search results with already-selected values so chips don't disappear
  const assignUserSearchOptions = useMemo(() => {
    const fromSearch = userSearchResults.map((u) => ({
      value: String(u.id),
      label: `${u.name} (${u.email})`,
    }));
    // Ensure selected values are always present
    const selectedIds = new Set(fromSearch.map((o) => o.value));
    const extras = assignTargets.filter((s) => !selectedIds.has(s.value));
    return [...extras, ...fromSearch];
  }, [userSearchResults, assignTargets]);

  const assignGranteeSearchOptions = useMemo(() => {
    const fromSearch = granteeSearchResults.map((u) => ({
      value: String(u.id),
      label: `${u.name} (${u.email})`,
    }));
    const selectedIds = new Set(fromSearch.map((o) => o.value));
    const extras = assignGrantees.filter((s) => !selectedIds.has(s.value));
    return [...extras, ...fromSearch];
  }, [granteeSearchResults, assignGrantees]);

  // ─── Add rule ───────────────────────────────────────────────────────────────
  const handleAddRule = useCallback(async () => {
    if (!newGranteeId) return;
    setIsAddingSaving(true);
    setError(null);
    try {
      const newRule = await createPermissionRule({
        targetType: newTargetType,
        targetId: newTargetType === 'all' ? null : newTargetId,
        permission,
        granteeType: 'user',
        granteeId: newGranteeId,
      });
      setRules((prev) => {
        const exists = prev.some((r) => r.id === newRule.id);
        return exists ? prev : [...prev, newRule];
      });
      setNewTargetType('all');
      setNewTargetId(null);
      setNewGranteeId(null);
      setShowAddForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add rule');
    } finally {
      setIsAddingSaving(false);
    }
  }, [newTargetType, newTargetId, newGranteeId, permission]);

  // ─── Add per-user assignment (multi-select: targets × grantees) ────────────
  const handleAddAssignment = useCallback(async () => {
    if (assignTargets.length === 0 || assignGrantees.length === 0) return;
    setIsAssignSaving(true);
    setError(null);
    try {
      // Build all combinations of target × grantee
      const payloads = assignTargets.flatMap((t) =>
        assignGrantees.map((g) => ({
          targetType: 'user' as const,
          targetId: t.value,
          permission,
          granteeType: 'user' as const,
          granteeId: g.value,
        })),
      );

      let newRules: PermissionRule[];
      if (payloads.length === 1) {
        const rule = await createPermissionRule(payloads[0]);
        newRules = [rule];
      } else {
        newRules = await createPermissionRulesBulk(payloads);
      }

      setRules((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const unique = newRules.filter((r) => !existingIds.has(r.id));
        return [...prev, ...unique];
      });
      setAssignTargets([]);
      setAssignGrantees([]);
      setShowAssignForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add assignment');
    } finally {
      setIsAssignSaving(false);
    }
  }, [assignTargets, assignGrantees, permission]);

  // ─── Delete rule ────────────────────────────────────────────────────────────
  const handleDeleteRule = useCallback(async (ruleId: string) => {
    setDeletingRuleId(ruleId);
    setError(null);
    try {
      await deletePermissionRule(ruleId);
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete rule');
    } finally {
      setDeletingRuleId(null);
    }
  }, []);

  // ─── Computed: per-user rules (only users with direct assignments) ──────────
  const perUserRules = useMemo(() => {
    const userRules = rules.filter((r) => r.targetType === 'user');
    // Group by target user
    const grouped = new Map<string, PermissionRule[]>();
    for (const r of userRules) {
      if (!r.targetId) continue;
      const existing = grouped.get(r.targetId) ?? [];
      existing.push(r);
      grouped.set(r.targetId, existing);
    }
    return grouped;
  }, [rules]);

  const perUserRows = useMemo((): ResolvedUserRow[] => {
    const rows: ResolvedUserRow[] = [];
    for (const [userId, userRules] of perUserRules) {
      const user = userCache.get(userId);
      // Deduplicate grantees by granteeType+granteeId (keep first rule per unique grantee)
      const seen = new Set<string>();
      const grantees: ResolvedUserRow['grantees'] = [];
      for (const r of userRules) {
        const key = `${r.granteeType}:${r.granteeId}`;
        if (!seen.has(key)) {
          seen.add(key);
          grantees.push({
            granteeType: r.granteeType,
            granteeId: r.granteeId,
            source: 'direct' as const,
            ruleId: r.id,
          });
        }
      }
      rows.push({
        mondayUserId: userId,
        name: user?.name ?? `User ${userId}`,
        email: user?.email ?? '',
        grantees,
      });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [perUserRules, userCache]);

  // Broad rules (team/all) displayed separately
  const broadRules = useMemo(
    () => rules.filter((r) => r.targetType !== 'user'),
    [rules],
  );

  // ─── Filtered & paginated per-user rows ───────────────────────────────────
  const filteredPerUserRows = useMemo(() => {
    if (!tableSearchQuery.trim()) return perUserRows;
    const q = tableSearchQuery.toLowerCase();
    return perUserRows.filter((row) =>
      row.name.toLowerCase().includes(q) ||
      row.email.toLowerCase().includes(q),
    );
  }, [perUserRows, tableSearchQuery]);

  const totalFilteredRows = filteredPerUserRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredRows / TABLE_PAGE_SIZE));

  // Reset to first page when search changes
  useEffect(() => {
    setTablePage(0);
  }, [tableSearchQuery]);

  // Clamp page if rows shrink (e.g. after deletion)
  const safePage = Math.min(tablePage, totalPages - 1);
  const paginatedRows = useMemo(() => {
    const start = safePage * TABLE_PAGE_SIZE;
    return filteredPerUserRows.slice(start, start + TABLE_PAGE_SIZE);
  }, [filteredPerUserRows, safePage]);

  const showingFrom = totalFilteredRows === 0 ? 0 : safePage * TABLE_PAGE_SIZE + 1;
  const showingTo = Math.min((safePage + 1) * TABLE_PAGE_SIZE, totalFilteredRows);

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="rp-permission-rules__loading">
        <Loader size={Loader.sizes.MEDIUM} />
      </div>
    );
  }

  return (
    <div className="rp-permission-rules">
      {/* Error banner */}
      {error && (
        <div className="rp-permission-rules__error-banner">
          <Text style={{ color: '#e2445c' }}>{error}</Text>
        </div>
      )}

      {/* ── Section 1: Rules (team / all) ─────────────────────────────────── */}
      <div className="rp-permission-rules__section">
        <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
          Rules
        </Text>
        <Text
          type={Text.types.TEXT2}
          style={{ color: '#676879', marginTop: 4, marginBottom: 12 }}
        >
          Rules apply to all matching users automatically (team-wide or
          workspace-wide).
        </Text>

        {/* Existing rules list */}
        <div className="rp-permission-rules__rules-list">
          {broadRules.length === 0 && (
            <Text
              type={Text.types.TEXT2}
              style={{ color: '#676879', fontStyle: 'italic', marginBottom: 8 }}
            >
              No rules configured yet.
            </Text>
          )}
          {broadRules.map((rule) => (
            <div key={rule.id} className="rp-permission-rules__rule-chip">
              <Text type={Text.types.TEXT2}>
                {formatTarget(rule)}
                <span style={{ color: '#676879', margin: '0 8px' }}>→</span>
                {formatGrantee(rule)}
              </Text>
              <IconButton
                icon={CloseSmall}
                size={IconButton.sizes.XS}
                kind={IconButton.kinds.TERTIARY}
                ariaLabel="Delete rule"
                onClick={() => handleDeleteRule(rule.id)}
                disabled={deletingRuleId === rule.id}
              />
            </div>
          ))}
        </div>

        {/* Add rule form */}
        {showAddForm ? (
          <div className="rp-permission-rules__add-form">
            <Flex gap={Flex.gaps.SMALL} align={Flex.align.END} wrap>
              {/* Target type */}
              <div className="rp-permission-rules__add-field">
                <Text type={Text.types.TEXT2} style={{ marginBottom: 4 }}>
                  Target
                </Text>
                <Dropdown
                  size={Dropdown.sizes.SMALL}
                  options={targetTypeOptions}
                  value={targetTypeOptions.find((o) => o.value === newTargetType)}
                  onChange={(option) => {
                    const val = (option as { value: string })?.value ?? 'all';
                    setNewTargetType(val as TargetType);
                    setNewTargetId(null);
                  }}
                  className="rp-permission-rules__dropdown--sm"
                />
              </div>

              {/* Target ID (for team or user) */}
              {newTargetType === 'team' && (
                <div className="rp-permission-rules__add-field">
                  <Text type={Text.types.TEXT2} style={{ marginBottom: 4 }}>
                    Team
                  </Text>
                  <Dropdown
                    size={Dropdown.sizes.SMALL}
                    options={teamOptions}
                    value={newTargetId ? teamOptions.find((o) => o.value === newTargetId) ?? null : null}
                    onChange={(option) => setNewTargetId((option as { value: string })?.value ?? null)}
                    placeholder="Select team..."
                    className="rp-permission-rules__dropdown--md"
                  />
                </div>
              )}

              {newTargetType === 'user' && (
                <div className="rp-permission-rules__add-field">
                  <Text type={Text.types.TEXT2} style={{ marginBottom: 4 }}>
                    User (type 2+ chars)
                  </Text>
                  <Dropdown
                    size={Dropdown.sizes.SMALL}
                    options={userSearchOptions}
                    value={newTargetId ? userSearchOptions.find((o) => o.value === newTargetId) ?? null : null}
                    onChange={(option) => {
                      const val = (option as { value: string })?.value ?? null;
                      setNewTargetId(val);
                      if (val) {
                        const user = userSearchResults.find((u) => String(u.id) === val);
                        if (user) cacheUser(user);
                      }
                    }}
                    onInputChange={(newValue, actionMeta) => {
                      if (actionMeta.action === 'input-change') {
                        setUserSearchQuery(newValue);
                      }
                    }}
                    placeholder="Search user..."
                    isLoading={isUserSearching}
                    className="rp-permission-rules__dropdown--md"
                  />
                </div>
              )}

              <Text type={Text.types.TEXT2} style={{ color: '#676879', paddingBottom: 8 }}>
                →
              </Text>

              {/* Grantee (user only — team grantees are no longer supported
                  for new rules; see granteeTypeOptions comment). */}
              <div className="rp-permission-rules__add-field">
                <Text type={Text.types.TEXT2} style={{ marginBottom: 4 }}>
                  User (type 2+ chars)
                </Text>
                <Dropdown
                  size={Dropdown.sizes.SMALL}
                  options={granteeSearchOptions}
                  value={
                    newGranteeId
                      ? granteeSearchOptions.find((o) => o.value === newGranteeId) ?? null
                      : null
                  }
                  onChange={(option) => {
                    const val = (option as { value: string })?.value ?? null;
                    setNewGranteeId(val);
                    if (val) {
                      const user = granteeSearchResults.find((u) => String(u.id) === val);
                      if (user) cacheUser(user);
                    }
                  }}
                  onInputChange={(newValue, actionMeta) => {
                    if (actionMeta.action === 'input-change') {
                      setGranteeSearchQuery(newValue);
                    }
                  }}
                  placeholder="Search user..."
                  isLoading={isGranteeSearching}
                  className="rp-permission-rules__dropdown--md"
                />
              </div>

              <Button
                size={Button.sizes.SMALL}
                onClick={handleAddRule}
                loading={isAddingSaving}
                disabled={!newGranteeId || (newTargetType !== 'all' && !newTargetId)}
              >
                Add
              </Button>
              <Button
                size={Button.sizes.SMALL}
                kind={Button.kinds.TERTIARY}
                onClick={() => {
                  setShowAddForm(false);
                  setNewTargetType('all');
                  setNewTargetId(null);
                  setNewGranteeId(null);
                }}
              >
                Cancel
              </Button>
            </Flex>
          </div>
        ) : (
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.TERTIARY}
            leftIcon={Add}
            onClick={() => setShowAddForm(true)}
          >
            Add Rule
          </Button>
        )}
      </div>

      {/* ── Section 2: Per-User Assignments ──────────────────────────────── */}
      <div className="rp-permission-rules__section" style={{ marginTop: 24 }}>
        <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
          Per-User Assignments
        </Text>
        <Text
          type={Text.types.TEXT2}
          style={{ color: '#676879', marginTop: 4, marginBottom: 12 }}
        >
          Direct assignments for individual users. Search for a user to add a
          new assignment.
        </Text>

        {/* Add per-user assignment form (multi-select) */}
        {showAssignForm ? (
          <div className="rp-permission-rules__add-form" style={{ marginBottom: 16 }}>
            <Flex gap={Flex.gaps.SMALL} align={Flex.align.END} wrap>
              <div className="rp-permission-rules__add-field">
                <Text type={Text.types.TEXT2} style={{ marginBottom: 4 }}>
                  User(s) — type 2+ chars to search
                </Text>
                <Dropdown
                  size={Dropdown.sizes.SMALL}
                  multi
                  multiline
                  options={assignUserSearchOptions}
                  value={assignTargets}
                  onChange={(selected) => {
                    const arr = (selected ?? []) as DropdownOptionObj[];
                    setAssignTargets(arr);
                    // Cache all selected users
                    for (const opt of arr) {
                      const user = userSearchResults.find((u) => String(u.id) === opt.value);
                      if (user) cacheUser(user);
                    }
                  }}
                  onOptionRemove={(removedOption) => {
                    if (removedOption) {
                      setAssignTargets((prev) =>
                        prev.filter((o) => o.value !== (removedOption as DropdownOptionObj).value),
                      );
                    }
                  }}
                  onInputChange={(newValue, actionMeta) => {
                    if (actionMeta.action === 'input-change') {
                      setUserSearchQuery(newValue);
                    }
                  }}
                  placeholder="Search users..."
                  isLoading={isUserSearching}
                  closeMenuOnSelect={false}
                  className="rp-permission-rules__dropdown--lg"
                />
              </div>

              <Text type={Text.types.TEXT2} style={{ color: '#676879', paddingBottom: 8 }}>
                →
              </Text>

              <div className="rp-permission-rules__add-field">
                <Text type={Text.types.TEXT2} style={{ marginBottom: 4 }}>
                  {granteeLabel} — type 2+ chars to search
                </Text>
                <Dropdown
                  size={Dropdown.sizes.SMALL}
                  multi
                  multiline
                  options={assignGranteeSearchOptions}
                  value={assignGrantees}
                  onChange={(selected) => {
                    const arr = (selected ?? []) as DropdownOptionObj[];
                    setAssignGrantees(arr);
                    for (const opt of arr) {
                      const user = granteeSearchResults.find((u) => String(u.id) === opt.value);
                      if (user) cacheUser(user);
                    }
                  }}
                  onOptionRemove={(removedOption) => {
                    if (removedOption) {
                      setAssignGrantees((prev) =>
                        prev.filter((o) => o.value !== (removedOption as DropdownOptionObj).value),
                      );
                    }
                  }}
                  onInputChange={(newValue, actionMeta) => {
                    if (actionMeta.action === 'input-change') {
                      setGranteeSearchQuery(newValue);
                    }
                  }}
                  placeholder="Search users..."
                  isLoading={isGranteeSearching}
                  closeMenuOnSelect={false}
                  className="rp-permission-rules__dropdown--lg"
                />
              </div>

              <Button
                size={Button.sizes.SMALL}
                onClick={handleAddAssignment}
                loading={isAssignSaving}
                disabled={assignTargets.length === 0 || assignGrantees.length === 0}
              >
                Add
              </Button>
              <Button
                size={Button.sizes.SMALL}
                kind={Button.kinds.TERTIARY}
                onClick={() => {
                  setShowAssignForm(false);
                  setAssignTargets([]);
                  setAssignGrantees([]);
                }}
              >
                Cancel
              </Button>
            </Flex>
          </div>
        ) : (
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.TERTIARY}
            leftIcon={Add}
            onClick={() => setShowAssignForm(true)}
            style={{ marginBottom: 16 }}
          >
            Add Assignment
          </Button>
        )}

        {/* Search + table of existing per-user assignments */}
        {perUserRows.length === 0 ? (
          <div className="rp-permission-rules__empty">
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              No per-user assignments yet. Use "Add Assignment" above or create
              team/all rules for broader coverage.
            </Text>
          </div>
        ) : (
          <>
            {/* Search bar */}
            <Flex
              gap={Flex.gaps.SMALL}
              align={Flex.align.CENTER}
              style={{ marginBottom: 16 }}
            >
              <div style={{ width: 280 }}>
                <TextField
                  size={TextField.sizes.SMALL}
                  placeholder="Search by user name or email..."
                  value={tableSearchQuery}
                  onChange={(value: string) => setTableSearchQuery(value)}
                  iconName={Search}
                />
              </div>
              <Text type={Text.types.TEXT2} style={{ color: '#676879', whiteSpace: 'nowrap' }}>
                {totalFilteredRows} assignment{totalFilteredRows !== 1 ? 's' : ''}
              </Text>
            </Flex>

            {filteredPerUserRows.length === 0 ? (
              <div className="rp-permission-rules__empty">
                <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                  No assignments match your search.
                </Text>
              </div>
            ) : (
              <>
                <table className="rp-settings-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>{granteeLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row) => (
                      <tr key={row.mondayUserId}>
                        <td>
                          <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
                            {row.name}
                          </Text>
                        </td>
                        <td>
                          <Text type={Text.types.TEXT2}>{row.email}</Text>
                        </td>
                        <td>
                          <Flex gap={Flex.gaps.XS} align={Flex.align.CENTER} wrap>
                            {row.grantees.map((g, i) => (
                              <span
                                key={`${g.granteeType}-${g.granteeId}-${i}`}
                                className="rp-permission-rules__grantee-chip"
                              >
                                <Text type={Text.types.TEXT2}>
                                  {formatGranteeName(g.granteeType, g.granteeId)}
                                </Text>
                                <IconButton
                                  icon={CloseSmall}
                                  size={IconButton.sizes.XXS}
                                  kind={IconButton.kinds.TERTIARY}
                                  ariaLabel="Remove"
                                  onClick={() => handleDeleteRule(g.ruleId)}
                                  disabled={deletingRuleId === g.ruleId}
                                />
                              </span>
                            ))}
                          </Flex>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination controls */}
                {totalPages > 1 && (
                  <div className="rp-permission-rules__pagination">
                    <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                      Showing {showingFrom}–{showingTo} of {totalFilteredRows}
                    </Text>
                    <div className="rp-permission-rules__pagination-buttons">
                      <Button
                        size={Button.sizes.SMALL}
                        kind={Button.kinds.TERTIARY}
                        disabled={safePage === 0}
                        onClick={() => setTablePage((p) => Math.max(0, p - 1))}
                      >
                        Previous
                      </Button>
                      <Text
                        type={Text.types.TEXT2}
                        style={{ color: '#676879', padding: '0 8px' }}
                      >
                        Page {safePage + 1} of {totalPages}
                      </Text>
                      <Button
                        size={Button.sizes.SMALL}
                        kind={Button.kinds.TERTIARY}
                        disabled={safePage >= totalPages - 1}
                        onClick={() => setTablePage((p) => Math.min(totalPages - 1, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PermissionRulesTab;
